/**
 * Numbered pager items: a 3-page window that slides with the current page.
 * The last 2 pages stay anchored at the end; once the window has slid past
 * page 3, page 1 stays anchored at the start. Hidden ranges collapse into
 * non-clickable ellipses.
 *
 *   page 1 of 20:  1 2 3 … 19 20
 *   page 3 of 20:  … 2 3 4 … 19 20
 *   page 4 of 20:  1 … 3 4 5 … 19 20
 *  page 10 of 20:  1 … 9 10 11 … 19 20
 *  page 17 of 20:  1 … 16 17 18 19 20
 *  page 20 of 20:  1 … 18 19 20
 */

export type PageItem = number | 'ellipsis';

export function pageItems(currentPage: number, totalPages: number): PageItem[] {
  if (!Number.isFinite(totalPages) || totalPages <= 0) return [];
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const current = Math.min(Math.max(1, currentPage), totalPages);
  const start = Math.max(1, Math.min(current - 1, totalPages - 2));
  const end = Math.min(totalPages, start + 2);
  const items: PageItem[] = [];
  if (start > 2) {
    items.push(1, 'ellipsis');
  } else if (start === 2) {
    items.push('ellipsis');
  }
  for (let p = start; p <= end; p += 1) items.push(p);
  if (end <= totalPages - 3) {
    items.push('ellipsis', totalPages - 1, totalPages);
  } else if (end === totalPages - 2) {
    items.push(totalPages - 1, totalPages);
  } else if (end === totalPages - 1) {
    items.push(totalPages);
  }
  return items;
}

/** 1-based inclusive row range for a page, e.g. page 2 of 20/per page -> { start: 21, end: 40 }. */
export function pageRange(
  page: number,
  perPage: number,
  totalCount: number
): { start: number; end: number } {
  if (totalCount <= 0 || perPage <= 0) return { start: 0, end: 0 };
  const start = (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, totalCount);
  return { start, end };
}
